import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { FaIconComponent, FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import Utils from "src/app/_helpers/utils";
import {
  Article,
  ArticleHandling,
  ArticleSummary,
  ArticleTemplate,
  EditEventType,
  Operation,
  OperationData,
  OperationDataAttachment,
  OperationDataCombined,
  OperationFile,
  Part,
  StoreUnit,
  Technician,
  Ticket,
  Checklist,
  ChecklistItem,
} from "src/app/_models";
import { MetaDataArticleOutput, OperationMetadata, OperationMetadataStep } from "src/app/_models/operation.interface";
import { ArticleService, OperationService, StoreService, TicketService, WorkflowService, ChecklistService } from "src/app/_services";
import { LocalStorageService } from "src/app/_services/localstore.service";
import { EditInputComponent } from "../../_shared/edit-input/edit-input.component";
import { OperationSubpageStepArticleComponent } from "../operation-subpage-step-article/operation-subpage-step-article.component";
import { ArticleFilterPipe, ArticleTemplateFilterPipe, TechnicianFilterPipe } from "src/app/_pipes";
import { NgxFilesizeModule } from "ngx-filesize";
import { FileInputComponent } from "../../_shared/file-input/file-input.component";
import { WorkflowUploadComponent } from "src/app/workflow-upload/workflow-upload.component";
import { MtResultComponent } from "../../mt-result/mt-result.component";
import { MtOverride } from "src/app/_models/mt-override.interface";
import { OperationRequirementsComponent } from "../operation-requirements/operation-requirements.component";

@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    FontAwesomeModule,
    EditInputComponent,
    OperationSubpageStepArticleComponent,
    ArticleTemplateFilterPipe,
    NgxFilesizeModule,
    TechnicianFilterPipe,
    ArticleFilterPipe,
    FileInputComponent,
    WorkflowUploadComponent,
    MtResultComponent,
    OperationRequirementsComponent,
  ],
  selector: "hawk-operation-subpage",
  templateUrl: "./operation-subpage.component.html",
  styleUrls: ["./operation-subpage.component.scss"],
})
export class OperationSubpageComponent implements OnInit, OnChanges {
  @Input() operation: Operation;
  @Input() ticket: Ticket;
  @Input() technicians: Technician[];
  @Input() articleTemplates: ArticleTemplate[];
  @Input() articleSummary: ArticleSummary;
  @Input() isBookkeeper: boolean;
  @Input() canReject: boolean;
  @Output() reloadTicket = new EventEmitter<boolean>();
  @Output() selectStep = new EventEmitter<OperationMetadata>();

  public parts: Part[] = [];
  public files: OperationFile[] = [];
  public operationData: OperationData[] = [];
  public operationDataCombined: OperationDataCombined[] = [];
  public operationDataSortMode: 'entry' | 'location' = 'entry'; // Default: nach Eintragung
  public storeUnits: StoreUnit[] = [];
  public selectedPart: Part;
  public articleHandling: ArticleHandling[] = [];
  public checklist: Checklist;
  public checklistItems: ChecklistItem[] = [];
  public checklistViewMode: 'compact' | 'expanded' | 'table' = 'compact';
  public checklistShowDebug = false;
  public checklistHideUnanswered = false;
  public reportLinebreaks = false;
  public selectedTab = "overview";

  public editDescription = false;
  public editDescriptionAi = false;
  public editTechnicianReport = false;

  public editStatus = false;
  public editTravelStart = false;
  public editArrival = false;
  public editRepaired = false;
  public editFinished = false;
  public editDistance = false;
  public editOperationDate = false;
  public editDateFixed = false;
  public editTechnicians = false;
  public editInvoiceCustomer = false;
  public editInvoiceAmount = false;
  public editInvoicePOAmount = false;
  public editInvoicePOXAmount = false;
  public editInvoicePO15Amount = false;
  public editInvoicePartner = false;
  public editInvoicePartnerAmount = false;
  public editStepstatus = false;
  public editAiCorrect = false;
  public editItemCount = false;
  public editFileName: boolean[];
  public linkDeliveryNoteForm = false;
  public deliveryNoteNumberToLink = "";
  public importShipmentForm = false;
  public newMaterialForm = false;
  public newMaterialTerm = "";
  public newMaterial: {
    item_nr: string;
    description: string;
    article_template_id: number;
    amount: number;
    status: string;
    sn: string;
  };
  public newMaterialSelectedArticle: ArticleTemplate;

  public materialStatusOptions = ["", "Proposed", "Ordered", "Shipped", "booked", "wrongArticle", "notInPackage"];
  public statusOptions = [
    { id: "PLANNED", name: "geplant" },
    { id: "DONE", name: "erledigt" },
    { id: "REWORK", name: "Nacharbeiten erforderlich" },
  ];

  public newPartForm = false;
  public newPartTerm = "";
  public newArticleTemplateTerm = "";
  public newPart: {
    sn_removed: string;
    article_id: number;
    error_description: string;
  };
  public newPartSelectedArticle: Article;
  public sliceCountArticles = 5;

  public newSelectedArticleTemplate: ArticleTemplate;

  public badPartOnly = false;

  public techicianSearchboxValue = "";
  public selectedTechnicianIds: number[] = [];

  public searchArticles: Article[] = [];

  public stepWarning =
    "Achtung: Dieser Schritt ist nicht umkehrbar, er löst automatische Prozesse aus. Die App kann danach keine weiteren Daten mehr für diesen Auftrag übermitteln!";

  public shippingImportform: UntypedFormGroup;
  public shippingImportStatus = "new";
  public shippingImporterrorIds: string[] = [];
  public shippingImporterrorText: string;
  public shippingImporterrorLog: string;

  public mtRejectionReasons = [
    { key: "TSRC001", title: "Falscher Dienstanbieter" },
    { key: "TSRC002", title: "Datenformat falsch" },
    { key: "TSRC003", title: "Pflichtinformationen fehlen" },
    { key: "TSRC004", title: "Datenqualität" },
    {
      key: "TSRC005",
      title:
        "Falsche Klassifikation (Abgeschlossen) - Techniker vor Ort bestätigt, dass der Artikel falsch klassifiziert wurde und er nicht der richtige Dienstleister ist.",
    },
    { key: "TSRC006", title: "Unzureichende Qualifikation" },
  ];
  public mtRejectionSelectedReason = null;
  public mtRejectionText = "Einsatz konnte nicht übernommen werden, für Rückfragen stehen wir gerne zur Verfügung";
  public mtOverrides: MtOverride[] = [];

  public mtInstalledItemKeys: string[] = [];

  public mtOverridesSn: string;
  public mtOverridesSST: string;
  public mtOverridesKey: string;
  public mtOverridesValue: string;
  public mtOverridesDelete: boolean;

  constructor(
    private operationService: OperationService,
    private articleService: ArticleService,
    private storeService: StoreService,
    private ticketService: TicketService,
    private workflowService: WorkflowService,
    private formBuilder: UntypedFormBuilder,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private checklistService: ChecklistService,
    private localStore: LocalStorageService,
  ) { }

  ngOnInit(): void {
    this.loadChecklistViewSettings();
    this.setEmptyMaterial();
    this.setEmptyPart();
    this.loadParts();
    this.loadFiles();
    this.loadOperationData();
    this.loadOperationDataCombined();
    this.loadArticleHandling();
    this.updateSelectedTechnicians();
    this.updateSelectedTechnicians();
    this.loadArticlesTemplates();
    this.loadChecklist();

    this.shippingImportform = this.formBuilder.group({
      importfile: [""],
    });

    this.workflowService.getOperationWorkflowsPresent(this.operation.id).subscribe(
      (data) => {
        // console.log(data)
      },
      (err) => {
        console.error(err);
      },
    );

    this.setMtOverrides();
    this.setMtFields();
    this.mtInstalledItemKeys = Object.keys(this.ticket?.mt_items || {});
  }

  private loadChecklistViewSettings() {
    const viewMode = this.localStore.getItem<string>('checklist.viewMode');
    if (viewMode === 'compact' || viewMode === 'expanded' || viewMode === 'table') {
      this.checklistViewMode = viewMode;
    }

    const showDebug = this.localStore.getItem<boolean>('checklist.showDebug');
    if (typeof showDebug === 'boolean') {
      this.checklistShowDebug = showDebug;
    }
  }

  public onChecklistViewModeChange(mode: 'compact' | 'expanded' | 'table') {
    this.checklistViewMode = mode;
    this.localStore.setItem('checklist.viewMode', mode);
  }

  public onChecklistDebugChange(value: boolean) {
    this.checklistShowDebug = value;
    this.localStore.setItem('checklist.showDebug', value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["ticket"] && !changes["ticket"].isFirstChange()) {
      this.setMtFields();
      this.mtInstalledItemKeys = Object.keys(this.ticket?.mt_items || {});
    }
  }

  public setMtFields() {
    if (this.ticket) {
      this.mtRejectionSelectedReason = this.ticket.mt_rejection_code;
      this.mtRejectionText = this.ticket.mt_rejection_reason;
    }
  }

  public setMtOverrides() {
    this.ticketService.getMtOverrides(this.ticket.id).subscribe({
      next: (data) => {
        console.log(data);
        this.mtOverrides = data;
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  public loadParts(selectLast = false) {
    this.selectedPart = null;
    this.operationService.getParts(this.operation.id.toString()).subscribe(
      (data) => {
        this.parts = data;
        this.editFileName = [];

        this.parts.forEach((p, index) => {
          this.editFileName[index] = false;
        });

        if (selectLast) {
          this.selectedPart = this.parts[this.parts.length - 1];
          console.log(this.parts, this.selectedPart);
        }
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public loadArticleHandling() {
    this.operationService.getArticleHandling(this.operation.id.toString()).subscribe(
      (data) => {
        this.articleHandling = data;
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public loadArticlesTemplates() {
    this.articleService.getArticlesTemplatesByProject(this.ticket.project_id).subscribe(
      (data) => {
        this.articleTemplates = data;

        console.log("articleTemplates", this.articleTemplates);
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public loadSearchArticles() {
    this.articleService.getArticlesByTemplate(this.newSelectedArticleTemplate.id).subscribe(
      (data) => {
        this.searchArticles = data;
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public updateSelectedTechnicians() {
    this.selectedTechnicianIds = this.operation.technicians.map((technician) => technician.id);
  }

  public selectedTechnicians(): Technician[] {
    //  return this.technicians.filter(technician => this.selectedTechnicianIds.includes(technician.id))

    // I considered the approach above - a more elegant solution - but the correct order of ids was more importent
    // than the last bit of performance, so this map with an n² lookup was used. Its fast enough.
    return this.selectedTechnicianIds
      .map((techId) => this.technicians.filter((technician) => technician.id === techId).pop())
      .filter((technician) => technician !== undefined);
  }

  public addTechnician(technicianId: number) {
    this.selectedTechnicianIds.push(technicianId);
  }

  public keysFor(hash): string[] {
    return Object.keys(hash).sort((a, b) => this.sortCleaned(a, b));
  }

  public sortCleaned(a, b): number {
    const cleanA = a ? this.cleanString(a) : "";
    const cleanB = b ? this.cleanString(b) : "";

    if (cleanA < cleanB) {
      return -1;
    }
    if (cleanA > cleanB) {
      return 1;
    }

    return 0;
  }

  public cleanString(input): string {
    if (!input) {
      return "";
    }
    return input.trim().replace(/\s{2}/g, "");
  }

  public removeTechnician(technicianId: number) {
    this.selectedTechnicianIds = this.selectedTechnicianIds.filter((selectedTechId) => selectedTechId !== technicianId);
  }

  public openTechnicianForm() {
    this.updateSelectedTechnicians();
    this.editTechnicians = true;
  }

  public countMaterials(materialArray): number {
    return materialArray.map((item) => item.amount).reduce((acc, count) => acc + count, 0);
  }

  public getSNForValueEntry(entry: MetaDataArticleOutput): string[] {
    if (entry === null || entry === undefined) {
      return [];
    }
    const snObjects = Object.values(entry);
    return snObjects.map((item) => {
      return item.sn;
    });
  }

  public selectMaterialArticleTemplate(articleTemplate: ArticleTemplate) {
    this.newMaterialSelectedArticle = articleTemplate;
    this.newMaterial.article_template_id = articleTemplate.id;

    return false;
  }

  public unselectMaterialArticleTemplate() {
    this.newMaterialSelectedArticle = null;
    this.newMaterial.article_template_id = null;

    return false;
  }

  public selectPartArticle(article: Article) {
    this.newPartSelectedArticle = article;
    this.newPart.article_id = article.id;

    this.resetArticleSliceCount();

    return false;
  }

  public unselectPartArticle() {
    this.newPartSelectedArticle = null;
    this.newPart.article_id = null;
    this.resetArticleSliceCount();
    this.newPartTerm = "";

    return false;
  }

  public selectPartArticleTemplate(articleTemplate: ArticleTemplate) {
    this.newSelectedArticleTemplate = articleTemplate;
    this.resetArticleSliceCount();

    this.articleService.getArticlesByTemplate(this.newSelectedArticleTemplate.id).subscribe(
      (data) => {
        this.searchArticles = data;
      },
      (err) => {
        console.error(err);
      },
    );

    return false;
  }

  public unselectPartArticleTemplate() {
    this.newSelectedArticleTemplate = null;
    this.newArticleTemplateTerm = "";
    this.newPartSelectedArticle = null;
    this.newPart.article_id = null;
    this.newPartTerm = "";
    this.resetArticleSliceCount();

    return false;
  }

  public increaseArticleSliceCount() {
    this.sliceCountArticles += 5;

    return false;
  }

  public resetArticleSliceCount() {
    this.sliceCountArticles = 5;
  }

  public technicianNames(): string {
    return this.operation.technicians.map((t) => t.full_name).join(", ");
  }

  public convertLinebreaks(text: string, replace: boolean = true): string {
    if (!text) {
      return "";
    }

    return replace ? text.replace(new RegExp("\n", "g"), "<br />") : text;
  }

  public toggleReportLinebreaks() {
    this.reportLinebreaks = !this.reportLinebreaks;
  }

  public stepStatusEditable(): boolean {
    if (!this.operation.metadata) {
      return false;
    }

    if (this.operation.metadata_done) {
      return false;
    }

    if (this.status() !== "erledigt" && this.status() !== "Nacharbeiten erforderlich") {
      return false;
    }

    return true;
  }

  public toggleStepStatus() {
    if (!this.stepStatusEditable()) {
      return;
    }

    this.editStepstatus = !this.editStepstatus;
  }

  public onShippingFileChange(event) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.shippingImportform.get("importfile").setValue(file);
    }
  }

  public onShippingFileSubmit() {
    const formData = new FormData();
    formData.append("file", this.shippingImportform.get("importfile").value);
    this.shippingImportStatus = "running";

    this.ticketService.ticketShipmentImport(formData).subscribe(
      (res) => {
        this.shippingImportStatus = "done";
        this.shippingImporterrorIds = res.errors || [];
        this.shippingImporterrorLog = res.log || "";

        this.reloadTicket.emit(true);
      },
      (err) => {
        console.error(err);
        this.shippingImportStatus = "error";
        this.shippingImporterrorText = "es ist etwas schief gegangen.";
      },
    );
  }

  public isTabSelected(tab: string): boolean {
    if (this.selectedTab) {
      return tab === this.selectedTab;
    } else {
      return false;
    }
  }

  public getMetadataImagePath(stepValue): string {
    return Utils.metadataImagePath(stepValue);
  }

  /**
   * status
   */
  public status(): string {
    if (!this.operation) {
      return "";
    }

    switch (this.operation.status) {
      case "DONE": {
        return "erledigt";
        break;
      }
      case "REWORK": {
        return "Nacharbeiten erforderlich";
        break;
      }
      case "PLANNED": {
        return "geplant";
        break;
      }
      default: {
        return "???";
        break;
      }
    }
  }

  public stepStatus(): string {
    if (!this.operation) {
      return "";
    }

    if (!this.operation.metadata) {
      return "keine Schritte vorhanden";
    }

    if (this.operation.metadata_done) {
      return "abgeschlossen";
    } else {
      return "nicht abgeschlossen!";
    }
  }

  public formatArticleTemplate(articleTemplate: ArticleTemplate): string {
    return [
      articleTemplate.description,
      articleTemplate.product_category,
      articleTemplate.vendor,
      articleTemplate.id,
    ].join(", ");
  }

  public formatArticle(article: Article): string {
    return [article.rma, article.description, article.serial_number].join(", ");
  }

  public updateOperation(operationData: Operation) {
    this.operation = operationData;
  }

  public signaturePath(base64Url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(base64Url);
  }

  public saveUploadedFile(files: FileList) {
    this.operationService.saveUploadedFile(this.operation.id, files).subscribe(
      (data) => {
        console.log(data);
        this.loadFiles();
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public deleteFile(filename: string) {
    const doIt = window.confirm("Ja, Datei (" + filename + ") löschen.");

    if (doIt) {
      this.operationService.destroyFile(this.operation.id, filename).subscribe(
        (data) => {
          console.log(data);
          this.loadFiles();
        },
        (err) => {
          console.error(err);
        },
      );
    }
  }

  public loadFiles() {
    this.operationService.getFiles(this.operation.id).subscribe(
      (data) => {
        this.files = data;
      },
      (err) => {
        console.error(err);
      },
    );
  }

  /**
   * Lädt alle OperationData für die aktuelle Operation
   */
  public loadOperationData() {
    this.operationService.getOperationData(this.operation.id).subscribe({
      next: (data) => {
        this.operationData = data;
        this.sortOperationData(); // Sortierung nach dem Laden anwenden
        console.log(`Loaded ${data.length} OperationData records`, data);
      },
      error: (err) => {
        console.error('Error loading OperationData:', err);
      }
    });
  }

  /**
   * Lädt OperationData aus der kombinierten View (Checklisten-optimiert)
   */
  public loadOperationDataCombined() {
    this.operationService.getOperationDataCombined(this.operation.id).subscribe({
      next: (data) => {
        this.operationDataCombined = data || [];
      },
      error: (err) => {
        console.error('Error loading combined OperationData:', err);
        this.operationDataCombined = [];
      }
    });
  }

  /**
   * Sortiert die OperationData basierend auf dem aktuellen Sortierungs-Modus
   */
  public sortOperationData() {
    if (this.operationDataSortMode === 'entry') {
      // Sortierung nach Eintragung (ID/Zeitstempel)
      this.operationData.sort((a, b) => a.id.localeCompare(b.id));
    } else if (this.operationDataSortMode === 'location') {
      // Sortierung nach Ort: item_sn (leer zuerst), value_type, value_key, id
      this.operationData.sort((a, b) => {
        // 1. item_sn (leer zuerst)
        const snA = a.item_sn || '';
        const snB = b.item_sn || '';
        if (snA !== snB) {
          if (snA === '') return -1;
          if (snB === '') return 1;
          return snA.localeCompare(snB);
        }

        // 2. value_type
        const typeA = a.value_type || '';
        const typeB = b.value_type || '';
        if (typeA !== typeB) {
          return typeA.localeCompare(typeB);
        }

        // 3. value_key
        const keyA = a.value_key || '';
        const keyB = b.value_key || '';
        if (keyA !== keyB) {
          return keyA.localeCompare(keyB);
        }

        // 4. id als finaler Tie-Breaker
        return a.id.localeCompare(b.id);
      });
    }
  }

  /**
   * Setzt den Sortierungs-Modus und sortiert die Daten neu
   */
  public setOperationDataSortMode(mode: 'entry' | 'location') {
    this.operationDataSortMode = mode;
    this.sortOperationData();
  }

  public updateFilename(oldFilename: string, newFilename: EditEventType, index: number) {
    this.operationService.renameFile(this.operation.id, oldFilename, newFilename).subscribe(
      (data) => {
        console.log(data);
        this.editFileName[index] = false;
        this.loadFiles();
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public saveMaterial() {
    this.ticketService.createMaterial(this.operation.ticket_id, this.newMaterial).subscribe(
      (data) => {
        console.log(data);
        this.setEmptyMaterial();
        this.ticket.material.push(data);
        this.newMaterialForm = false;
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public setEmptyMaterial() {
    this.newMaterial = {
      item_nr: "",
      description: "",
      article_template_id: null,
      amount: 1,
      status: "",
      sn: "",
    };
  }

  public savePart() {
    const part: Part = {
      operation_id: this.operation.id,
      oldsn: this.newPart.sn_removed,
      description: this.newPart.error_description,
      article_template_id: this.newSelectedArticleTemplate.id,
    };

    if (this.badPartOnly) {
      part.bad_part_only = true;
    } else {
      (part.newsn = this.newPartSelectedArticle.serial_number),
        (part.article_id = this.newPartSelectedArticle.id),
        (part.rma = this.newPartSelectedArticle.rma);
      part.bad_part_only = false;
    }

    this.newPartForm = false;

    this.operationService.createPart(part).subscribe(
      (_) => {
        this.loadParts();
        this.setEmptyPart();
        this.reloadDeliveryNotes();
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public setEmptyPart() {
    this.newPart = {
      article_id: null,
      error_description: "",
      sn_removed: "",
    };
    this.unselectPartArticle();
  }

  public deletePart() {
    const doIt = window.confirm("Diesen Eintrag wirklich löschen?");

    if (doIt) {
      this.operationService.destroyPart(this.selectedPart).subscribe(
        (_) => {
          this.loadParts();
        },
        (err) => {
          console.error(err);
        },
      );
    }
  }

  public navigateNewDN() {
    const link = ["/a/lieferscheine/neu?ticket_id=", this.ticket.id].join("");
    window.open(link, "_blank");
  }

  public linkDeliveryNote() {
    this.ticketService.linkDeliveryNote(this.ticket.id, this.deliveryNoteNumberToLink).subscribe(
      (data) => {
        this.linkDeliveryNoteForm = false;
        this.ticket.delivery_notes = data;

        this.cdr.detectChanges();
      },
      (error) => {
        alert("Dieser Lieferschein konnte nicht gespeichert werden.");
      },
    );
  }

  public reloadDeliveryNotes() {
    // waiting here until old hawk has a chance to update. The method will return
    //  before timeout ends, this is intended.
    setTimeout(() => {
      this.ticketService.getDeliveryNotes(this.ticket.id).subscribe(
        (data) => {
          this.ticket.delivery_notes = data;
        },
        (error) => {
          alert("Fehler");
        },
      );
    }, 5000);
  }

  public deleteDN(deliveryNoteId: number) {
    const doIt = window.confirm("Ja, Verlinkung wirklich entfernen (Info: Lieferschein bleibt wird nicht gelöscht)");

    if (doIt) {
      this.ticketService.unlinkDeliveryNote(this.ticket.id, deliveryNoteId).subscribe(
        (data) => {
          this.linkDeliveryNoteForm = false;
          this.ticket.delivery_notes = data;
        },
        (error) => {
          alert("Dieser Lieferschein konnte nicht gespeichert werden.");
        },
      );
    }
  }

  public editPart(part: Part) {
    console.log(part);
    this.selectedPart = part;
  }

  public updatePart() {
    this.operationService.savePart(this.selectedPart).subscribe(
      (_) => {
        this.loadParts();
      },
      (error) => {
        alert("Konnte teil nicht speichern." + error);
      },
    );
  }

  public cancelPart() {
    this.loadParts();
  }

  public saveDescription(event: EditEventType) {
    this.metaUpdate("description", event);
    this.editDescription = false;
  }

  public saveDescriptionAi(event: EditEventType) {
    this.metaUpdate("description_ai", event);
    this.editDescriptionAi = false;
  }

  public saveTechnicianReport(event: EditEventType) {
    this.metaUpdate("report", event);
    this.editTechnicianReport = false;
  }

  public saveStatus(event: EditEventType) {
    this.metaUpdate("status", event);
    this.editStatus = false;
  }

  public saveTravelStart(event: EditEventType) {
    this.metaUpdate("date_travel_start", event);
    this.editTravelStart = false;
  }

  public saveArrival(event: EditEventType) {
    this.metaUpdate("date_on_site", event);
    this.editArrival = false;
  }

  public saveRepaired(event: EditEventType) {
    this.metaUpdate("date_repaired", event);
    this.editRepaired = false;
  }

  public saveFinished(event: EditEventType) {
    this.metaUpdate("date_finished", event);
    this.editFinished = false;
  }

  public saveDistance(event: EditEventType) {
    this.metaUpdate("distance", event);
    this.editDistance = false;
  }

  public saveOperationDate(newDate: any) {
    this.metaUpdate("operation_date", newDate);
    this.editOperationDate = false;
  }

  public saveDateFixed(newVal: any) {
    this.metaUpdate("date_fixed", newVal);
    this.editDateFixed = false;
  }

  public saveInvoiceCustomer(event: EditEventType) {
    this.metaUpdate("invoice", event);
    this.editInvoiceCustomer = false;
  }

  public saveInvoiceAmount(event: EditEventType) {
    this.metaUpdate("invoice_amount", event);
    this.editInvoiceAmount = false;
  }

  public saveInvoicePOAmount(event: EditEventType) {
    this.metaUpdate("invoice_po_order_amount", event);
    this.editInvoicePOAmount = false;
  }

  public saveInvoicePOXAmount(event: EditEventType) {
    this.metaUpdate("invoice_pox_order_amount", event);
    this.editInvoicePOXAmount = false;
  }

  public saveInvoicePO15Amount(event: EditEventType) {
    this.metaUpdate("invoice_po15_order_amount", event);
    this.editInvoicePO15Amount = false;
  }

  public saveInvoicePartner(event: EditEventType) {
    this.metaUpdate("invoice_partner", event);
    this.editInvoicePartner = false;
  }

  public saveInvoicePartnerAmount(event: EditEventType) {
    this.metaUpdate("invoice_partner_amount", event);
    this.editInvoicePartnerAmount = false;
  }

  public saveTechnicians() {
    this.metaUpdate("technician_ids", this.selectedTechnicianIds);
    this.editTechnicians = false;
  }

  public saveItemCount(event: EditEventType) {
    this.metaUpdate("item_count", event);
    this.editItemCount = false;
  }

  public saveOperationAiCorrect(event: EditEventType) {
    this.metaUpdate("ai_calculation_correct", event);
    this.editAiCorrect = false;
  }

  public markStepDone() {
    const doIt = window.confirm(this.stepWarning);

    if (doIt) {
      this.operationService.markStepsDone(this.operation.id).subscribe(
        (data) => {
          window.location.reload();
        },
        (error) => {
          alert("Konnte Änderung nicht durchführen!");
        },
      );
    }
  }

  private metaUpdate(key, value) {
    const changeData = {};
    changeData[key] = value;

    this.operationService.updateOperation(this.operation.id.toString(), { operation: changeData }).subscribe(
      (data) => {
        this.updateOperation(data);
      },
      (error) => { },
    );
  }

  public articleInstallationFieldType(value): "sn_list" | "sn" | "no_sn" | "noSn" | "unknown" {
    if (value instanceof Array) {
      return "sn_list";
    }

    if (value && value.sn) {
      return "sn";
    }

    if (value && value.no_sn) {
      return "no_sn";
    }

    if (value && value.noSn) {
      return "noSn";
    }

    return "unknown";
  }

  public transferStep(step: OperationMetadata) {
    this.selectStep.emit(step);
  }

  public sendRejection() {
    this.ticketService
      .updateToRejectTicket(this.ticket.id.toString(), {
        mt_rejection_code: this.mtRejectionSelectedReason,
        mt_rejection_reason: this.mtRejectionText,
      })
      .subscribe(
        (_) => {
          this.reloadTicket.emit(true);
        },
        (error) => {
          alert("Fehler beim Speichern der Ablehnung.");
        },
      );
  }

  public addOverride() {
    this.ticketService
      .addMtOverride(this.ticket.id, {
        ticket_id: this.ticket.id,
        sn: this.mtOverridesSn,
        sst_id: this.mtOverridesSST,
        mt_key: this.mtOverridesKey,
        mt_value: this.mtOverridesValue,
        delete_item: this.mtOverridesDelete,
      })
      .subscribe({
        next: (_) => {
          this.setMtOverrides();
        },
        error: (error) => {
          alert("Fehler beim Speichern der Überschreibung.");
        },
      });
  }

  public deleteOverride(override: MtOverride) {
    const doIt = window.confirm("Überschreibung löschen?");

    if (doIt) {
      this.ticketService.deleteMtOverride(override.id).subscribe({
        next: (_) => {
          this.setMtOverrides();
        },
        error: (error) => {
          alert("Fehler beim Löschen der Überschreibung.");
        },
      });
    }
  }

  public selectOnlyBadpart() {
    this.badPartOnly = true;

    this.resetArticleSliceCount();

    return false;
  }

  /**
   * Gibt alle verfügbaren Werte für einen OperationData-Eintrag zurück
   */
  public getOperationDataValues(operationData: OperationData | OperationDataCombined): Array<{ key: string, value: any, type?: string }> {
    const values = [];

    // value_key wird jetzt in der separaten "Type & Key" Spalte angezeigt, nicht hier
    if (operationData.value_string) {
      values.push({ key: 'value_string', value: operationData.value_string });
    }
    if (operationData.value_number !== null && operationData.value_number !== undefined) {
      values.push({ key: 'value_number', value: operationData.value_number });
    }
    if (operationData.value_bool !== null && operationData.value_bool !== undefined) {
      values.push({ key: 'value_bool', value: operationData.value_bool ? 'Ja' : 'Nein' });
    }
    if (operationData.value_blob) {
      // Prüfe ob es sich um ein FILENAME# Bild handelt
      if (operationData.value_blob.startsWith('FILENAME#')) {
        values.push({ key: 'value_blob', value: operationData.value_blob, type: 'filename_image' });
      }
      // Prüfe ob es sich um ein Base64-Bild handelt
      else if (this.isBase64Image(operationData.value_blob)) {
        values.push({ key: 'value_blob', value: operationData.value_blob, type: 'base64_image' });
      } else {
        values.push({ key: 'value_blob', value: operationData.value_blob });
      }
    }
    if (operationData.attachment_id) {
      values.push({ key: 'attachment_id', value: operationData.attachment_id });
    }

    return values;
  }  /**
   * Prüft ob ein String ein Base64-encodiertes Bild ist
   */
  public isBase64Image(value: string): boolean {
    return value.startsWith('data:image/') || value.match(/^[A-Za-z0-9+/=]+$/) !== null;
  }

  /**
   * Extrahiert den Dateinamen aus einem FILENAME# String
   */
  public extractFilenameFromBlob(value: string): string | null {
    if (value.startsWith('FILENAME#')) {
      const filename = value.substring(9); // Remove "FILENAME#" prefix
      return filename.toLowerCase(); // Dateinamen immer in Kleinbuchstaben
    }
    return null;
  }

  /**
   * Generiert den vollständigen Pfad zu einer Bild-Datei
   */
  public getOperationImagePath(filename: string): string {
    // Stelle sicher, dass der Dateiname in Kleinbuchstaben ist
    const lowercaseFilename = filename.toLowerCase();
    return `/r/api/v1/operations/${this.operation.id}/file/${lowercaseFilename}`;
  }

  /**
   * Öffnet ein Base64-Bild in einem neuen Tab
   */
  public openBase64ImageInNewTab(base64Value: string): void {
    const imageUrl = base64Value.startsWith('data:') ? base64Value : `data:image/png;base64,${base64Value}`;
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head><title>Base64 Bild</title></head>
          <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#f5f5f5;">
            <img src="${imageUrl}" style="max-width:100%; max-height:100%; object-fit:contain;" />
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  }

  public loadChecklist() {
    this.checklistService.getChecklistForOperation(this.operation.id).subscribe(
      (data) => {
        this.checklist = data;
        if (this.checklist) {
          // If the API returns items included (as requested), we don't need a separate call.
          // But my TS interface might not reflect that yet.
          // The updated controller returns `checklist.as_json(include: :checklist_items)`.
          // So `data.checklist_items` should be available if I update the interface.
          // However, for safety/compatibility with my previous logic:
          if (data['checklist_items']) {
            this.checklistItems = data['checklist_items'];
          } else {
            this.loadChecklistItems(this.checklist.id);
          }
        }
      },
      (err) => {
        console.warn("No active checklist found for this ticket/project", err);
      }
    );
  }

  public loadChecklistItems(checklistId: number) {
    this.checklistService.getChecklistItems(checklistId).subscribe(
      (data) => {
        this.checklistItems = data;
      },
      (err) => {
        console.error(err);
      }
    );
  }

  public getOperationDataForQuestion(item: ChecklistItem): OperationData[] {
    if (!item) return [];

    const source = this.operationDataCombined.length > 0 ? this.operationDataCombined : this.operationData;
    let datas: OperationData[] = [];

    if (item.item_type === 'article') {
      const articleValueType = item.item_subtype === 'installed' ? 'installed_article'
        : item.item_subtype === 'removed' ? 'removed_article' : null;
      if (articleValueType) {
        datas = source.filter(d => d.value_type === articleValueType && d.value_key == item.id.toString()) as OperationData[];

        // Fallback: Matching über reference-Feld bei Checklisten-Versionswechsel
        if (datas.length === 0 && item.reference) {
          datas = source.filter(d => {
            if (d.value_type !== articleValueType) return false;
            const combined = d as OperationDataCombined;
            return combined.checklist_item?.reference?.toString() === item.reference.toString();
          }) as OperationData[];
        }
      }
    } else if (item.item_type === 'subchecklist') {
      datas = source.filter(d => this.isSubchecklistEntryForParent(d, item.id)) as OperationData[];
    } else {
      datas = source.filter(d => d.value_type === 'app_checklist' && d.value_key == item.id.toString()) as OperationData[];

      // Fallback: Matching über reference-Feld bei Checklisten-Versionswechsel
      if (datas.length === 0 && item.reference) {
        datas = source.filter(d => {
          if (d.value_type !== 'app_checklist') return false;
          const combined = d as OperationDataCombined;
          return combined.checklist_item?.reference?.toString() === item.reference.toString();
        }) as OperationData[];
      }
    }

    return datas;
  }

  private isSubchecklistEntryForParent(entry: OperationData | OperationDataCombined, parentItemId: number): boolean {
    const parentId = entry.parent_id?.toString();
    if (parentId && parentId === parentItemId.toString()) {
      return true;
    }

    if (!entry.value_key) {
      return false;
    }

    const parentFromKey = this.extractParentIdFromValueKey(entry.value_key);
    if (entry.value_type === 'subchecklist' && parentFromKey === parentItemId.toString()) {
      return true;
    }

    // Prüfe ob der Eintrag zu einem Item gehört das in der
    // Subchecklist dieses Parent-Items liegt (z.B. Artikel)
    const parentItem = this.checklistItems?.find(i => i.id === parentItemId);
    if (parentItem?.data) {
      const data = typeof parentItem.data === 'string' ? JSON.parse(parentItem.data) : parentItem.data;
      const subChecklistId = data?.checklist;
      if (subChecklistId) {
        const combined = entry as OperationDataCombined;
        if (combined.checklist_item?.checklist_id == subChecklistId) {
          return true;
        }
      }
    }

    return false;
  }

  private extractParentIdFromValueKey(valueKey?: string): string | null {
    if (!valueKey) return null;
    const instancePart = valueKey.split(':')[0];
    const match = instancePart.match(/^(\d+)[-_]/) || instancePart.match(/^(\d+)$/);
    return match ? match[1] : null;
  }

  private extractIterationKeyFromValueKey(valueKey?: string): string | null {
    if (!valueKey) return null;
    return valueKey.split(':')[0] || null;
  }

  private extractQuestionIdFromValueKey(valueKey?: string): string | null {
    if (!valueKey) return null;
    const parts = valueKey.split(':');
    const candidate = parts.length > 1 ? parts[1] : parts[0];
    const match = candidate.match(/(\d+)/);
    return match ? match[1] : candidate;
  }

  public getChecklistItemsForDisplay(): ChecklistItem[] {
    const items = [...(this.checklistItems || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    if (!this.checklistHideUnanswered) {
      return items;
    }

    return items.filter((item) => this.hasAnswerForItem(item));
  }

  public hasAnswerForItem(item: ChecklistItem): boolean {
    if (!item) return false;
    if (item.item_type === 'subchecklist') {
      const groups = this.getSubchecklistIterations(item);
      return groups.some((group) => this.groupHasAnswers(group.entries));
    }

    const entries = this.getOperationDataForQuestion(item) as Array<OperationData | OperationDataCombined>;
    return this.groupHasAnswers(entries);
  }

  public groupHasAnswers(entries: Array<OperationData | OperationDataCombined>): boolean {
    return entries.some((entry) => this.isOperationDataAnswered(entry));
  }

  public isOperationDataAnswered(entry: OperationData | OperationDataCombined): boolean {
    const values = this.getOperationDataValues(entry);
    if (values.length > 0) return true;

    const attachmentList = this.getOperationDataAttachments(entry);
    return attachmentList.length > 0;
  }

  public getOperationDataAttachments(entry: OperationData | OperationDataCombined): OperationDataAttachment[] {
    if (entry.operation_data_attachments && entry.operation_data_attachments.length > 0) {
      return entry.operation_data_attachments;
    }

    if ((entry as OperationDataCombined).attachment) {
      return [(entry as OperationDataCombined).attachment as OperationDataAttachment];
    }

    return [];
  }

  public getSubchecklistIterations(item: ChecklistItem): Array<{ iterationKey: string; label: string; entries: OperationDataCombined[] }> {
    const entries = this.getSubchecklistEntries(item);

    const groups = new Map<string, OperationDataCombined[]>();
    entries.forEach((entry) => {
      const key = entry.iteration_id || this.extractIterationKeyFromValueKey(entry.value_key) || 'default';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(entry);
    });

    const sortedKeys = Array.from(groups.keys()).sort();

    return sortedKeys.map((key, index) => ({
      iterationKey: key,
      label: key === 'default' ? `Iteration ${index + 1}` : `Iteration ${key}`,
      entries: groups.get(key) || []
    }));
  }

  public getSubchecklistQuestionGroups(entries: OperationDataCombined[]): Array<{ questionKey: string; questionText: string; label: string; entries: OperationDataCombined[] }> {
    const groups = new Map<string, OperationDataCombined[]>();
    entries.forEach((entry) => {
      const questionKey = this.extractQuestionIdFromValueKey(entry.value_key) || entry.value_key || entry.id;
      if (!groups.has(questionKey)) {
        groups.set(questionKey, []);
      }
      groups.get(questionKey)!.push(entry);
    });

    return Array.from(groups.entries()).map(([questionKey, groupEntries]) => {
      const questionText = groupEntries.find((e) => e.checklist_item?.text)?.checklist_item?.text || '';

      return {
        questionKey,
        questionText,
        label: questionText || `#${questionKey}`,
        entries: groupEntries
      };
    });
  }

  private getSubchecklistEntries(item: ChecklistItem): OperationDataCombined[] {
    const combinedSource = this.operationDataCombined || [];
    if (combinedSource.length > 0) {
      return combinedSource.filter(d => this.isSubchecklistEntryForParent(d, item.id));
    }

    const fallbackEntries = this.operationData.filter(d => this.isSubchecklistEntryForParent(d, item.id));
    return this.normalizeOperationEntries(fallbackEntries);
  }

  private normalizeOperationEntries(entries: Array<OperationData | OperationDataCombined>): OperationDataCombined[] {
    return entries.map((entry) => {
      if ((entry as OperationDataCombined).checklist_item || (entry as OperationDataCombined).attachment) {
        return entry as OperationDataCombined;
      }

      const base = entry as OperationData;
      return {
        id: base.id,
        operation_id: base.operation_id,
        item_sn: base.item_sn,
        value_type: base.value_type,
        value_key: base.value_key,
        value_string: base.value_string,
        value_number: base.value_number,
        value_bool: base.value_bool,
        value_blob: base.value_blob,
        created_by_id: base.created_by_id,
        created_at: base.created_at,
        updated_at: base.updated_at,
        assigned_user_ids: base.assigned_user_ids,
        status_closed: base.status_closed,
        visible_app: base.visible_app,
        attachment_id: base.attachment_id,
        iteration_id: base.iteration_id,
        parent_id: base.parent_id,
        operation_data_attachments: base.operation_data_attachments,
        article: base.article,
      } as OperationDataCombined;
    });
  }

  public getChecklistTableRows(): Array<{ questionId?: string; questionText: string; entries: OperationDataCombined[]; isEmpty?: boolean }> {
    const rows: Array<{ questionId?: string; questionText: string; entries: OperationDataCombined[]; isEmpty?: boolean }> = [];
    const items = this.getChecklistItemsForDisplay();

    items.forEach((item) => {
      if (item.item_type === 'subchecklist') {
        const iterations = this.getSubchecklistIterations(item);
        if (iterations.length === 0) {
          if (!this.checklistHideUnanswered) {
            rows.push({
              questionId: this.checklistShowDebug ? item.id.toString() : undefined,
              questionText: `Subcheckliste: ${item.text}`,
              entries: [],
              isEmpty: true
            });
          }
          return;
        }

        iterations.forEach((iteration) => {
          if (this.checklistHideUnanswered && !this.groupHasAnswers(iteration.entries)) {
            return;
          }

          const questionGroups = this.getSubchecklistQuestionGroups(iteration.entries);
          questionGroups.forEach((group) => {
            if (this.checklistHideUnanswered && !this.groupHasAnswers(group.entries)) {
              return;
            }

            rows.push({
              questionId: this.checklistShowDebug ? group.questionKey.toString() : undefined,
              questionText: `${item.text} · ${iteration.label} · ${group.label}`,
              entries: group.entries,
              isEmpty: group.entries.length === 0
            });
          });
        });
        return;
      }

      const entries = this.normalizeOperationEntries(this.getOperationDataForQuestion(item) || []);
      if (entries.length === 0) {
        if (!this.checklistHideUnanswered) {
          rows.push({
            questionId: this.checklistShowDebug ? item.id.toString() : undefined,
            questionText: item.text,
            entries: [],
            isEmpty: true
          });
        }
        return;
      }

      if (this.checklistHideUnanswered && !this.groupHasAnswers(entries)) {
        return;
      }

      rows.push({
        questionId: this.checklistShowDebug ? item.id.toString() : undefined,
        questionText: item.text,
        entries,
        isEmpty: entries.length === 0
      });
    });

    return rows;
  }

  /**
   * Findet OperationData-Einträge die keinem Checklist-Item zugeordnet werden konnten.
   * Zeigt diese als "Nicht zugeordnete Einträge" am Ende der Checkliste.
   */
  public getUnmatchedOperationData(): OperationDataCombined[] {
    if (!this.checklistItems?.length) return [];

    const source = this.operationDataCombined.length > 0 ? this.operationDataCombined : this.operationData;
    const relevantTypes = ['app_checklist', 'installed_article', 'removed_article', 'used_article'];
    const relevantEntries = source.filter(d => relevantTypes.includes(d.value_type));

    // Sammle alle IDs die bereits zugeordnet sind
    const matchedIds = new Set<string>();
    for (const item of this.checklistItems) {
      const matched = this.getOperationDataForQuestion(item);
      matched.forEach(d => matchedIds.add(d.id));
    }

    return this.normalizeOperationEntries(
      relevantEntries.filter(d => !matchedIds.has(d.id))
    );
  }
}
